import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as INBOX_CONVERSATIONS_QUERY_KEY } from './useGetInboxConversations';

export interface ApiRequest_Archive_Conversation {
  conversationSid: string;
  locationId?: number;
}

export interface ApiResponse_Archive_Conversation {
  success: boolean;
  message: string;
}

const METHOD = 'POST';
const ENDPOINT = 'conversation/:conversationSid/archive';

export const useArchiveConversation = (
  stateFns?: MutationStateFns<ApiResponse_Archive_Conversation>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ApiResponse_Archive_Conversation, AxiosError, ApiRequest_Archive_Conversation>(
    {
      mutationFn: async (data: ApiRequest_Archive_Conversation) =>
        handleApiCall<ApiResponse_Archive_Conversation, ApiRequest_Archive_Conversation>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { conversationSid: data.conversationSid })
        ),
      onSuccess: (data, variables) => {
        // Invalidate inbox conversations query to refresh the list
        queryClient.invalidateQueries({
          queryKey: INBOX_CONVERSATIONS_QUERY_KEY({ location_id: variables.locationId }),
          refetchType: 'all',
        });

        // Invalidate unread inbox count to update menu badge
        queryClient.invalidateQueries({
          predicate: query => {
            const { queryKey } = query;
            return Array.isArray(queryKey) && queryKey[1] === 'conversation/inbox/unread-count';
          },
          refetchType: 'all',
        });

        stateFns?.onSuccess?.(data);
      },
      onError: (err: AxiosError) => {
        enqueueSnackbar('Failed to archive conversation.', { variant: 'error' });
        stateFns?.onError?.(err);
      },
    }
  );
};
